import { rgba } from 'polished';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useErrorBoundary, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	GraphQLErrorMessage,
	Heading,
	Modal,
	Popover,
	QuillText,
	tokens,
} from '@treams/treams-ui';

const HeaderImage = styled.img`
	width: 100%;
	max-height: 200px;
	object-fit: cover;
	border-radius: 4px;
	border: 1px solid ${tokens.border.subtle};
	box-shadow: 0 2px 4px ${rgba(tokens.border.subtle, 0.5)};
`;

type Props = {
	announcementID: string;
};

export default function Announcement({ announcementID }: Props) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const [error, attempt] = useErrorBoundary();

	const { data, loading } = graphql.useAnnouncementQuery({
		variables: { id: announcementID },
	});

	const [markAsRead, { loading: marking }] = graphql.useMarkAnnouncementAsReadMutation();

	const announcement = data?.announcement;

	// Don't render if loading, no announcement, or already read
	if (loading || !announcement || announcement.isRead) {
		return null;
	}

	const handleContinue = attempt(async () => {
		await markAsRead({
			variables: {
				input: { announcementID },
			},
		});
	});

	return (
		<Popover backdrop center visible>
			<Modal width={600}>
				<Modal.Contents>
					<GraphQLErrorMessage error={error} />

					<Box mb={3}>
						{announcement.headerImage && (
							<HeaderImage
								src={announcement.headerImage.source.url}
								alt={localize(announcement.title).text}
							/>
						)}
					</Box>

					<Heading>{localize(announcement.title).text}</Heading>

					<Box pt={2}>
						<QuillText value={localize(announcement.content).text} />
					</Box>
				</Modal.Contents>
				<Modal.Footer>
					<Button title={t('common.continue')} onClick={handleContinue} loading={marking} />
				</Modal.Footer>
			</Modal>
		</Popover>
	);
}
